import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const FormModal = ({ isOpen, onClose, onSubmit, title, fields = [], initialData = null }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(initialData);
        if (initialData?.image_url) {
          setImagePreview(initialData?.image_url);
        }
      } else {
        const defaultValues = {};
        fields?.forEach((field) => {
          if (field?.type === 'checkbox') {
            defaultValues[field?.name] = false;
          } else if (field?.type === 'checkbox-group') {
            defaultValues[field?.name] = [];
          } else if (field?.type === 'number') {
            defaultValues[field?.name] = field?.defaultValue || '';
          } else {
            defaultValues[field?.name] = '';
          }
        });
        setFormData(defaultValues);
      }
      setErrors({});
      setImagePreview(null);
    }
  }, [isOpen, initialData, fields]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleImageChange = (name, e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader?.result);
      };
      reader?.readAsDataURL(file);
      
      // Store file in formData
      handleChange(name, file);
    }
  };

  const handleCheckboxGroupChange = (name, option, checked) => {
    const currentValues = formData?.[name] || [];
    const newValues = checked
      ? [...currentValues, option]
      : currentValues?.filter((v) => v !== option);
    handleChange(name, newValues);
  };

  const validate = () => {
    const newErrors = {};
    fields?.forEach((field) => {
      if (field?.required && !formData?.[field?.name]) {
        newErrors[field?.name] = `${field?.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      label: field?.label,
      required: field?.required,
      error: errors?.[field?.name],
    };

    switch (field?.type) {
      case 'image-upload':
        return (
          <div className="space-y-2">
            {commonProps?.label && (
              <label className="text-sm font-medium text-gray-700">
                {commonProps?.label}
                {commonProps?.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(field?.name, e)}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                <p className="text-xs text-gray-500 mt-1">Upload an image (JPG, PNG, max 5MB)</p>
              </div>
              {(imagePreview || formData?.[field?.name]) && (
                <div className="w-24 h-24 border border-gray-300 rounded-md overflow-hidden">
                  <img
                    src={imagePreview || formData?.[field?.name]}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
            {commonProps?.error && (
              <p className="text-sm text-red-600">{commonProps?.error}</p>
            )}
          </div>
        );

      case 'checkbox-group':
        return (
          <div className="space-y-2">
            {commonProps?.label && (
              <label className="text-sm font-medium text-gray-700">
                {commonProps?.label}
                {commonProps?.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <div className="grid grid-cols-2 gap-2 p-3 border border-gray-300 rounded-md max-h-48 overflow-y-auto">
              {field?.options?.map((option) => (
                <div key={option?.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={(formData?.[field?.name] || [])?.includes(option?.value)}
                    onChange={(e) => handleCheckboxGroupChange(field?.name, option?.value, e?.target?.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <label className="text-sm text-gray-700">{option?.label}</label>
                </div>
              ))}
            </div>
            {commonProps?.error && (
              <p className="text-sm text-red-600">{commonProps?.error}</p>
            )}
          </div>
        );

      case 'rating':
        return (
          <div className="space-y-2">
            {commonProps?.label && (
              <label className="text-sm font-medium text-gray-700">
                {commonProps?.label}
                {commonProps?.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleChange(field?.name, star)}
                  className="focus:outline-none"
                >
                  <Icon
                    name="Star"
                    size={24}
                    className={star <= (formData?.[field?.name] || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                  />
                </button>
              ))}
              <span className="text-sm text-gray-600 ml-2">
                {formData?.[field?.name] || 0} / 5
              </span>
            </div>
            {commonProps?.error && (
              <p className="text-sm text-red-600">{commonProps?.error}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <Select
            {...commonProps}
            value={formData?.[field?.name] || ''}
            onChange={(value) => handleChange(field?.name, value)}
            options={field?.options || []}
            placeholder={`Select ${field?.label}`}
          />
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            {commonProps?.label && (
              <label className="text-sm font-medium text-gray-700">
                {commonProps?.label}
                {commonProps?.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <textarea
              value={formData?.[field?.name] || ''}
              onChange={(e) => handleChange(field?.name, e?.target?.value)}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {commonProps?.error && (
              <p className="text-sm text-red-600">{commonProps?.error}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData?.[field?.name] || false}
              onChange={(e) => handleChange(field?.name, e?.target?.checked)}
              className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <label className="text-sm font-medium text-gray-700">{field?.label}</label>
          </div>
        );

      case 'number':
        return (
          <Input
            {...commonProps}
            type="number"
            value={formData?.[field?.name] || ''}
            onChange={(e) => handleChange(field?.name, e?.target?.value)}
            step={field?.step || '1'}
            min={field?.min}
            max={field?.max}
          />
        );

      default:
        return (
          <Input
            {...commonProps}
            type={field?.type || 'text'}
            value={formData?.[field?.name] || ''}
            onChange={(e) => handleChange(field?.name, e?.target?.value)}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="px-6 py-4 space-y-4">
            {fields?.map((field) => (
              <div key={field?.name}>{renderField(field)}</div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {initialData ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormModal;